import { useCallback, useMemo, useState } from "react";

import { toastCaseError } from "src/cases/caseView/toastCaseError";
import { useUpdateCaseTaskCompletion } from "src/cases/queries/cases";
import type { CaseDeserialized, CaseTask } from "src/cases/queries/casesTypes";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";

type UseTaskCompletionParams = {
  task: CaseTask;
  caseData?: CaseDeserialized;
  isDirty: boolean;
  canCompleteTask: boolean;
  saveForm: (
    formEtagOverride?: number,
    options?: { saveHints?: boolean },
  ) => Promise<void>;
  hasHints: boolean;
  approveAllHints: () => Promise<number | undefined>;
  onBack: () => void;
};

export const useTaskCompletion = ({
  task,
  caseData,
  isDirty,
  canCompleteTask,
  saveForm,
  hasHints,
  approveAllHints,
  onBack,
}: UseTaskCompletionParams) => {
  const caseId = caseData?.data.id;
  const { mutateAsync: updateTaskCompletion, isPending: isCompletionPending } =
    useUpdateCaseTaskCompletion(caseId);

  const [isPendingCompletion, setIsPendingCompletion] = useState(false);
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false);

  const completeTaskCore = useCallback(
    async (formEtagOverride?: number) => {
      if (!caseData) {
        logger.error("Case data is missing");
        return;
      }

      if (!task.isCompleted && isDirty) {
        try {
          await saveForm(
            formEtagOverride,
            formEtagOverride != null ? { saveHints: true } : undefined,
          );
        } catch {
          return;
        }
      }

      try {
        await updateTaskCompletion({
          taskKey: task.key,
          taskId: task.id,
          isCompleted: !task.isCompleted,
          etag: caseData.data.etag,
        });

        toastActions.success({
          title: task.isCompleted
            ? "Task marked as incomplete"
            : "Task completed",
        });
      } catch (error) {
        toastCaseError(
          error,
          "Failed to update task",
          "The task or case has been modified elsewhere. Please reload and try again.",
        );
      }
    },
    [task, caseData, updateTaskCompletion, isDirty, saveForm],
  );

  const handleCompleteTask = useCallback(async () => {
    if (hasHints && !task.isCompleted) {
      setIsPendingCompletion(true);
      return;
    }
    await completeTaskCore();
  }, [hasHints, task.isCompleted, completeTaskCore]);

  const handleConfirmWithHints = useCallback(async () => {
    let newFormEtag: number | undefined;
    try {
      newFormEtag = await approveAllHints();
    } catch (error) {
      logger.error("Error approving hints:", error);
      toastActions.failure({
        title: "Failed to approve suggestions",
        description: "An unexpected error occurred. Please try again.",
      });
      return;
    }

    await completeTaskCore(newFormEtag);
    setIsPendingCompletion(false);
  }, [completeTaskCore, approveAllHints]);

  const handleBack = useCallback(() => {
    if (isDirty) {
      setIsUnsavedModalOpen(true);
    } else {
      onBack();
    }
  }, [isDirty, onBack]);

  const handleSaveAndGoBack = useCallback(async () => {
    try {
      await saveForm();
      setIsUnsavedModalOpen(false);
      onBack();
    } catch {
      // saveForm already shows error toasts — keep modal open so user can retry
    }
  }, [saveForm, onBack]);

  const checkboxDisabled = !task.isCompleted && !canCompleteTask;

  const unsavedModal = useMemo(
    () => ({
      open: isUnsavedModalOpen,
      onClose: () => setIsUnsavedModalOpen(false),
      onConfirm: handleSaveAndGoBack,
    }),
    [isUnsavedModalOpen, handleSaveAndGoBack],
  );

  const hintsModal = useMemo(
    () => ({
      open: isPendingCompletion,
      onClose: () => setIsPendingCompletion(false),
      onConfirm: handleConfirmWithHints,
    }),
    [isPendingCompletion, handleConfirmWithHints],
  );

  return {
    isCompletionPending,
    checkboxDisabled,
    handleCompleteTask,
    handleBack,
    unsavedModal,
    hintsModal,
  };
};
